import { instanceToPlain } from 'class-transformer';
import { Component } from 'react';
import { cloneDeep } from 'xconvert-web-shared';
import { lazyInject } from '../../context/module';
import { FileUpload } from '../../models/file-upload';
import { Logger } from '../../models/logger';
import { UploadInProgressFile } from '../../models/upload-in-progress-file';
import { FileManagerService } from '../../services/file-manager.service';
import { FileProcessService } from '../../services/file-process.service';
import { UIContext } from '../../services/ui-context';
import { WebSocketService } from '../../services/websocket.service';
import { ConnectivityUtils } from '../../utils/connectivity.utils';
import { gifEstimateStore } from '../../utils/gif-estimate.store';
import { PageData } from '../../types/route-types';
import { OrientationUpdateEvent, ProcessTypes } from '../../types/types';
import { FileUploadUtils } from '../../utils/file-upoad.utils';
import { scheduleCoordinator } from '../../services/scheduling/schedule-coordinator';
import { isPerFileMergeType } from '../../utils/merge-type.utils';
import { ConvertOptionsContextV2 } from '../convert-options-v2/convert-options.context';
import FileCardComponent, { UpdateProcessProbedDataFnProvider } from '../ui/file-card/file-card.component';
import SortableComponent from '../ui/sortable/sortable.component';
// Two leaves, and both are deliberate under D10. `studio-gate.ts` imports NOTHING at all, and
// `views/hold-defaults.ts`'s only import is `import type { StudioDocument }`, which TypeScript
// erases outright -- so at run time neither adds an edge to this file's graph, let alone the
// container edge that crashes a webpack module eval (`file-convert.component.tsx:813-822`).
// Verified at source 2026-09-17; if `hold-defaults.ts` ever takes a value import, the constants
// move to a leaf of their own rather than this import being widened.
import { SOLO_HOLD_MS } from '../studio/views/hold-defaults';
import { pageAcceptsStudio } from '../studio/studio-gate';
// Type-only: `host.ts` is types-only itself (no runtime code, no React import), so this adds no
// edge to the graph at run time. It is the ONE definition of what a successful conversion hands
// back (FRAME_PICKER_DESIGN.md §6.1 host interface).
import type { ConvertedEvent } from '../frame-picker/host';

interface FileProcessEditorProps {
    initialFiles?: UploadInProgressFile<any>[];
    convertOptionsContext: ConvertOptionsContextV2;
    fromType?: string[];
    toType: string;
    processType: string;
    pageData: PageData;
    onFilesChange?: (files: UploadInProgressFile<any>[]) => void;
    /**
     * The user pressed Convert. Raised before anything is scheduled, because the only caller so
     * far needs to read a piece of its OWN state that a conversion is about to clear — the Studio
     * nudge banner, whose NUDGE_DECLINED is a fact about what the user did instead (§8).
     */
    onConversionStart?: () => void;
    /**
     * Every card in the batch has been handed to the scheduler. The event names the inputs and
     * the workflow ids the schedule responses carried (`FileUpload.workflowId`; absent on the
     * `{queued:true}` path, where the coordinator owns the card and the id is not known yet).
     * The existing caller ignores the argument and navigates; the picker's host mirrors it onto
     * `onConverted$` so the picker can record `sid → workflow` BEFORE `/downloads` (§6.1, §6.4).
     */
    onConversionSuccess?: (event: ConvertedEvent) => void;
    probeDataProvider?: UpdateProcessProbedDataFnProvider;
    hideRemoveButton?: boolean;
    children?: React.ReactNode;
}

interface FileProcessEditorState {
    files: UploadInProgressFile<any>[];
}

export class FileProcessEditorComponent extends Component<FileProcessEditorProps, FileProcessEditorState> {
    private readonly logger = Logger.getLogger('FileProcessEditorComponent');

    @lazyInject(FileProcessService) fileProcessService: FileProcessService;
    @lazyInject(WebSocketService) websocketService: WebSocketService;
    @lazyInject(UIContext) uiContext: UIContext;
    @lazyInject(FileManagerService) fileManagerService: FileManagerService;

    constructor(props: FileProcessEditorProps) {
        super(props);
        this.state = {
            files: props.initialFiles || []
        };
    }

    componentDidUpdate(prevProps: FileProcessEditorProps, prevState: FileProcessEditorState) {
        if (this.state.files !== prevState.files) {
            // The editor owns the file list, so it owns estimate-record lifecycle:
            // any entry that left the list (remove button, wholesale updateFiles)
            // drops its record and marks in-flight probes stale. Sorting keeps the
            // same entries, so nothing is dropped there.
            const currentIds = new Set(this.state.files.filter(f => f).map(f => f.id));
            prevState.files.filter(f => f && !currentIds.has(f.id))
                .forEach(f => gifEstimateStore.remove(f.id));
            if (this.props.onFilesChange) {
                this.props.onFilesChange(this.state.files);
            }
        }
    }

    componentWillUnmount() {
        // Page navigation / editor teardown: cancel every tracked estimate
        // (binding lifecycle guard — no leaked promises or timers).
        this.state.files.filter(f => f).forEach(f => gifEstimateStore.remove(f.id));
    }

    public addFiles(newFiles: UploadInProgressFile<any>[]) {
        this.setState(prevState => ({
            files: [...prevState.files, ...newFiles]
        }));
    }

    public updateFiles(files: UploadInProgressFile<any>[]) {
        this.setState({ files });
    }

    public getFiles() {
        return this.state.files;
    }

    removeFile(file: any) {
        this.setState(prevState => {
            const newFiles = prevState.files.filter(f => f.getFile() !== file);
            return { files: newFiles };
        });
    }

    orientationChanged(orientationData: OrientationUpdateEvent) {
        const uploadInProgressFile = this.state.files.find(fm => fm && fm.getFile() === orientationData.file);
        if (!uploadInProgressFile) {
            this.logger.trace1('Set orientation was called on a file that is not on uploadInProgressFiles list');
            return;
        }
        uploadInProgressFile.setOrientation(orientationData.orientation);
        this.forceUpdate();
    }

    sortOrderChanged(e: any) {
        // Optional: Notify parent if needed, but state update happens in sortEnded usually or here if real-time
    }

    sortEnded(e: any) {
        this.setState({ files: e });
    }

    /**
     * "A single static photo shows for 8 seconds" — STUDIO_DESIGN.md §9 item 2.
     *
     * xconfig cannot express a default that depends on the number of inputs (the Phase 0 ruling
     * says so and moved the rule here: `STUDIO_PLAN_P0/progress.md:9,58`), and an Options V2
     * summary carries no "the user touched this" bit. So the test is "the summary still says what
     * the freshly built context said": if it does, nobody chose the value and 8 is the better
     * default; if it does not, someone chose and we leave it alone.
     *
     * THE COST, RECORDED. A user who deliberately types `8` on a one-photo page is
     * indistinguishable from one who typed nothing — and gets 8 either way, which is the same
     * outcome. A user who types `3` keeps 3. That is the case this rule must not break, and the
     * cheapest rule that cannot break it.
     *
     * Scoped to the image→video family by the SAME predicate the Studio tab uses, so a one-file
     * PDF merge or a one-file compress is untouched. `SOLO_HOLD_MS` is milliseconds and
     * `imageDuration` is whole seconds — the one conversion, in one place.
     */
    private applySoloPhotoHold(summary: any, files: UploadInProgressFile<any>[] = this.state.files): any {
        if (!summary || !pageAcceptsStudio(this.props.pageData)) return summary;
        if (files.length !== 1) return summary;
        const defaults = this.props.convertOptionsContext.getDefaultSummary?.();
        const current = summary.imageDuration;
        if (current === undefined || defaults === undefined) return summary;
        if (String(current) !== String(defaults.imageDuration)) return summary;
        return { ...summary, imageDuration: String(SOLO_HOLD_MS / 1000) };
    }

    prepareMergeOptionsAndSnapshot(files: UploadInProgressFile<any>[] = this.state.files) {
        const summary = instanceToPlain(this.applySoloPhotoHold(this.props.convertOptionsContext.getSummary(), files));
        const summaryJson = JSON.parse(JSON.stringify(summary));
        const optionsSnapshot = cloneDeep(summaryJson);

        const orientationAngleList: string[] =
            files.map(fm => (fm.getOrientation() && fm.getOrientation().angle) || '0');
        const orientationFlipList: string[] =
            files.map(fm => (fm.getOrientation() && fm.getOrientation().isFliped) || 'false');
        summaryJson.orientationAngleList = orientationAngleList;
        summaryJson.orientationFlipList = orientationFlipList;
        return { options: summaryJson, optionsSnapshot };
    }

    /**
     * Determines if files should be merged into a single output.
     * Checks both processType and mergeType options.
     */
    private shouldMergeFiles(): boolean {
        const summary = this.props.convertOptionsContext.getSummary() as any;
        const mergeType = summary?.mergeType;
        if (isPerFileMergeType(mergeType)) return false;
        if (this.props.processType === ProcessTypes.MERGE || this.props.processType === ProcessTypes.ARCHIVE) {
            return true;
        }
        return mergeType === 'MERGE_PDF' || mergeType === 'MERGE_ARCHIVE' || mergeType === 'MERGE_VIDEO' || mergeType === 'MERGE_ANIMATED';
    }

    /**
     * Set while one batch's cards are being dispatched, so the queued/stalled
     * outcome produces AT MOST ONE toast for the whole batch rather than one
     * modal per file (plan §4.4: "Batch header row, one toast per batch, at most
     * one modal"). During the 2026-09-05 incident a 340-file batch would have
     * opened 340 "Connection Issue" dialogs on top of each other.
     */
    private batchQueuedNoticeShown = false;

    /** Quick convert's button: every staged file. */
    public async startConversion() {
        await this.convertFiles(this.state.files);
    }

    /**
     * Convert the given staged files — THE ONE request path for both tabs (FRAME_PICKER_DESIGN.md
     * §0 "Convert", §6.1 host interface, §11.0 R10).
     *
     * `startConversion()` calls it with the whole list; the picker calls it through
     * `FramePickerHost.convertFiles([selectedFile])` with one. Both therefore share
     * `processFileWithFeedback` and everything it decides: the enforcement-error branch, the
     * `{queued: true}` sentinel, the inverted error branch and the single per-batch toast. The
     * picker owns no request code and can never drift from Quick convert's.
     *
     * `files` is used instead of `this.state.files` throughout — including the solo-photo hold
     * and the orientation lists — so the picker's one-file call sees exactly what a one-file
     * Quick convert would. The list is not required to be a subset of the staged list; the
     * caller is trusted to pass entries it got from `stagedFiles$`.
     *
     * Resolves after every card has been handed to the scheduler, then raises
     * `onConversionSuccess` with the inputs and the workflow ids known at that moment.
     */
    public async convertFiles(files: UploadInProgressFile<any>[], opts: { quiet?: boolean } = {}) {
        this.props.onConversionStart?.();
        // Round 6 (R6-I5, UX#13): `quiet` = the caller shows its own progress (the Frame picker), so
        // the per-batch "Connecting to server" toast is not raised over it.
        const quiet = opts.quiet === true;
        const processPromises: Promise<void>[] = [];
        const fileUploads: FileUpload[] = [];
        this.batchQueuedNoticeShown = false;

        if (this.shouldMergeFiles()) {
            const { options, optionsSnapshot } = this.prepareMergeOptionsAndSnapshot(files);
            const fileUpload = FileUploadUtils.createMergeFileUpload(
                files,
                this.websocketService.getId(),
                this.props.processType as any,
                this.props.fromType as any,
                this.props.toType as any,
                options,
                optionsSnapshot,
                this.props.pageData
            );

            this.fileManagerService.addFileUpload(fileUpload);
            fileUploads.push(fileUpload);
            processPromises.push(this.processFileWithFeedback(fileUpload, quiet));
        } else {
            const created = FileUploadUtils.createSingleFileUploads(
                files,
                this.websocketService.getId(),
                this.props.processType as any,
                this.props.fromType as any,
                this.props.toType as any,
                this.applySoloPhotoHold(this.props.convertOptionsContext.getSummary(), files),
                this.props.pageData
            );

            created.forEach(fileUpload => {
                this.fileManagerService.addFileUpload(fileUpload);
                fileUploads.push(fileUpload);
                processPromises.push(this.processFileWithFeedback(fileUpload, quiet));
            });
        }

        await Promise.all(processPromises);

        if (this.props.onConversionSuccess) {
            this.props.onConversionSuccess({
                workflowIds: fileUploads.map(fu => fu?.workflowId).filter((id): id is string => !!id),
                files,
                // Round 14 (picker K4/N3): a card scheduled hands-off has no id yet; the picker watches these for it.
                uploads: fileUploads,
            });
        }
    }


    private processFileWithFeedback(fileUpload: FileUpload, quiet: boolean = false): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!quiet) this.uiContext.successToast('Starting conversion...', 'Connecting to server', 3000);
            this.fileProcessService.processFile(fileUpload).subscribe({
                next: (data) => {
                    // The hands-off path emits a `{queued:true}` sentinel as soon
                    // as the card has a state worth showing: the schedule call has
                    // NOT succeeded, so there is nothing to retrack — the card is
                    // already on the list and the coordinator owns it from here.
                    if ((data as any)?.queued === true) {
                        this.noteBatchQueued((data as any)?.stalled === true);
                        resolve();
                        return;
                    }
                    this.fileManagerService.retrackFileUpload(fileUpload);
                    this.logger.trace7(data);
                    resolve();
                },
                error: (err) => {
                    this.logger.error({ message: 'File process failed.', error: err, data: FileUpload.toLoggableObject(fileUpload) });
                    if (err?.isEnforcementError) {
                        // Keep file on card — don't remove. Set enforcement error for display.
                        fileUpload.result = { error: true, dataType: 'enforcement_error', data: JSON.stringify(err.enforcementData) };
                        fileUpload.ws = { processStage: 'COMPLETED', processStageSubState: 'FAILED', dataType: 'enforcement_error', data: err.enforcementData?.userMessage || 'Usage limit reached.' } as any;
                        this.fileManagerService.saveFileUploadToStorage(fileUpload);
                        resolve();
                        return;
                    }
                    // THE INVERTED ERROR BRANCH (plan §4.1, correction 16;
                    // clientQueuedState only). 2026-09-05: `removeFile` used to run
                    // HERE, before either dialog — and it fires
                    // `GET /file-manager/delete/<id>` on the upload, process AND
                    // download domains, so the uploaded bytes were destroyed before
                    // the "Connection Issue" dialog offered a Retry that could no
                    // longer work. Under the flag the ONLY failure that still
                    // deletes bytes is a deterministic 4xx (`scheduleTerminal`):
                    // a 5xx, a 409, a timeout and an offline browser all keep the
                    // card AND the file, and the card says so.
                    if (scheduleCoordinator.enabled && !err?.scheduleTerminal) {
                        this.noteBatchQueued(true);
                        resolve();
                        return;
                    }
                    this.fileManagerService.removeFile(fileUpload);
                    if (ConnectivityUtils.isConnectivityError(err) || ConnectivityUtils.isRetryableUploadError(err)
                        || err?.message?.includes('All requests failed')) {
                        this.uiContext.showErrorDialog({
                            title: 'Connection Issue',
                            text: 'Could not reach the processing server. This is usually caused by a network issue. Please check your connection and try again.',
                            confirmButtonText: 'Retry',
                            successCallback: () => {
                                this.fileManagerService.addFileUpload(fileUpload);
                                this.processFileWithFeedback(fileUpload).then(resolve).catch(reject);
                            },
                            showCancelButton: true,
                            cancelButtonText: 'Cancel',
                            cancelCallback: () => reject(err),
                        });
                    } else {
                        this.uiContext.showErrorDialog({
                            title: 'Error',
                            text: `Failed to ${this.props.processType} the file. ${err?.message || 'Please try again.'}`,
                        });
                        reject(err);
                    }
                }
            });
        });
    }

    /**
     * At most ONE toast for the whole batch (plan §4.4). No per-file modal: the
     * card itself says "Queued — server busy, retrying" / "Not started — your
     * file is still here", and the batch header on /downloads carries the single
     * "Retry all". Provenance: 2026-09-05 incident, reviewer finding SaaS #5.
     */
    private noteBatchQueued(stalled: boolean) {
        if (this.batchQueuedNoticeShown) return;
        this.batchQueuedNoticeShown = true;
        if (stalled) {
            this.uiContext.warnToast(
                'Not started yet',
                'The server is busy. Your files are safe — use "Retry all" on the downloads page when you are ready.',
                false, false,
            );
            return;
        }
        this.uiContext.successToast(
            'Queued',
            'The server is busy right now. We keep retrying — your files stay where they are.',
            6000,
        );
    }

    render() {
        return (
            <div className={`container-fluid g-0`}>
                <SortableComponent
                    items={this.state.files}
                    sortHandlerClass='sort-handler'
                    classes={['row g-0']}
                    orderChanged={(e) => this.sortOrderChanged(e)}
                    sortEnded={(e) => this.sortEnded(e)}>
                    {
                        this.state.files.filter(f => f).map((uploadInProgressFile, idx) => {
                            return <div key={`${uploadInProgressFile.uuid}-parent-${idx}`}
                                data-notsortable={false}
                                data-uniquekey={uploadInProgressFile.uuid}
                                className={`sortable ${uploadInProgressFile.file.type.indexOf('image') === -1 ? 'non-image' : 'image'} col-12`}
                            >
                                <FileCardComponent
                                    uploadInProgressFile={uploadInProgressFile}
                                    convertOptionsContext={this.props.convertOptionsContext}
                                    convertOptionCategories={this.props.convertOptionsContext} // Passing context as categories as per original usage
                                    updateProcessProbedDataFnProvider={this.props.probeDataProvider}
                                    toType={this.props.toType as any}
                                    fromType={this.props.fromType as any}
                                    processType={this.props.processType}
                                    removeFile={$event => this.removeFile($event)}
                                    hideRemoveButton={this.props.hideRemoveButton}
                                    orientationChanged={$event => this.orientationChanged($event)}>
                                </FileCardComponent>
                            </div>
                        })
                    }
                </SortableComponent>
                {this.props.children}
            </div>
        );
    }
}
